import { useContext, useEffect, useState } from 'react';

import { ChainlitContext, useConfig } from '@chainlit/react-client';

import { Markdown } from '@/components/Markdown';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useLayoutMaxWidth } from 'hooks/useLayoutMaxWidth';

export interface ButtonLinkProps {
  name?: string;
  displayName?: string;
  iconUrl?: string;
  url: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  type?: 'url' | 'modal';
  className?: string;
  mobile?: boolean;
}

export default function ButtonLink({
  name,
  displayName,
  iconUrl,
  url,
  target,
  type = 'url',
  className,
  mobile
}: ButtonLinkProps) {
  const apiClient = useContext(ChainlitContext);
  const { config } = useConfig();
  const layoutMaxWidth = useLayoutMaxWidth();

  const isJavascriptUrl = url.startsWith('javascript:');

  const [open, setOpen] = useState(false);
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (type === 'modal' && open && !markdownContent) {
      setLoading(true);

      const isPublicPath = url.startsWith('/public');
      const fetchUrl = isPublicPath ? apiClient.buildEndpoint(url) : url;

      fetch(fetchUrl, {
        headers: {
          'Content-Type': 'text/plain',
          Accept: 'text/plain, text/markdown, text/html, */*'
        }
      })
        .then((res) => {
          if (!res.ok) throw new Error(`Failed to load markdown from ${fetchUrl}`);
          return res.text();
        })
        .then((text) => setMarkdownContent(text))
        .catch((err) => {
          console.error(err);
          setMarkdownContent('Error loading content.');
        })
        .finally(() => setLoading(false));
    }
  }, [type, open, url, markdownContent, apiClient]);

  const handleClick = isJavascriptUrl
    ? (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        e.preventDefault();
        const script = url.slice(11); // Remove 'javascript:'
        try {
          // eslint-disable-next-line no-eval
          eval(script);
        } catch (error) {
          console.error('Error executing javascript url:', error);
        }
      }
    : undefined;

  const content = (
    <>
      {iconUrl ? (
        <img
          src={
            iconUrl.startsWith('/public')
              ? apiClient.buildEndpoint(iconUrl)
              : iconUrl
          }
          className={mobile ? 'h-5 w-5' : 'h-6 w-6'}
          alt={name}
        />
      ) : null}
      {displayName && <span>{displayName}</span>}
      {!iconUrl && !displayName && name ? <span>{name}</span> : null}
    </>
  );

  const buttonElement = (
    <Button
      variant="ghost"
      size={displayName ? 'default' : 'icon'}
      className={`text-muted-foreground hover:text-muted-foreground ${className || ''}`}
      onClick={isJavascriptUrl ? handleClick : undefined}
      asChild={!isJavascriptUrl && type !== 'modal'}
    >
      {type === 'modal' ? (
        <span className={`inline-flex items-center cursor-pointer ${mobile ? 'gap-2' : 'gap-1'}`}>
          {content}
        </span>
      ) : isJavascriptUrl ? (
        <span className={`inline-flex items-center cursor-pointer ${mobile ? 'gap-2' : 'gap-1'}`}>
          {content}
        </span>
      ) : (
        <a
          href={url}
          target={target ?? '_blank'}
          rel="noopener noreferrer"
          className={`inline-flex items-center ${mobile ? 'gap-2' : 'gap-1'}`}
        >
          {content}
        </a>
      )}
    </Button>
  );

  const trigger = (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {type === 'modal' ? (
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size={displayName ? 'default' : 'icon'}
                className={`text-muted-foreground hover:text-muted-foreground ${className || ''}`}
                onClick={isJavascriptUrl ? handleClick : undefined}
                type="button"
                aria-haspopup="dialog"
              >
                <span className={`inline-flex items-center cursor-pointer ${mobile ? 'gap-2' : 'gap-1'}`}>
                  {content}
                </span>
              </Button>
            </DialogTrigger>
          ) : (
            buttonElement
          )}
        </TooltipTrigger>
        <TooltipContent>{name}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  if (type === 'modal') {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        {trigger}
        <DialogContent className="flex flex-col h-screen w-screen max-w-screen max-h-screen border-none !rounded-none overflow-y-auto">
          <div
            className="mx-auto flex flex-col flex-grow gap-6"
            style={{ maxWidth: layoutMaxWidth }}
          >
            <DialogHeader>
              <DialogTitle>{displayName || name}</DialogTitle>
            </DialogHeader>
            <Markdown
              className="flex flex-col flex-grow overflow-y-auto"
              allowHtml={config?.features?.unsafe_allow_html}
              latex={config?.features?.latex}
              renderMarkdown={true}
            >
              {loading ? 'Loading...' : markdownContent}
            </Markdown>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return trigger;
}